import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { BookOpen, Calculator, GraduationCap } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './EducationSavingsCalculatorPage/components/CalculationForm';
import ResultsOverview from './EducationSavingsCalculatorPage/components/ResultsOverview';
import SavingsChart from './EducationSavingsCalculatorPage/components/SavingsChart';
import QuickAdjustmentSidebar from './EducationSavingsCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './EducationSavingsCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './EducationSavingsCalculatorPage/components/TipsSidebar';
import InfoContent from './EducationSavingsCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  educationLevel: 'bachelor',
  yearsUntilStart: '5',
  educationDuration: '4',
  currentSavings: '100000',
  monthlyContribution: '5000',
  expectedReturn: '5',
  inflationRate: '4',
  tuitionFeePerYear: '150000',
  livingExpensesPerYear: '120000',
  booksAndSuppliesPerYear: '25000',
  scholarshipAmount: '0',
  includeMaternityCapital: false,
  maternityCapitalAmount: '524500',
  includeRegionalPrograms: false,
  regionalSupportAmount: '0',
  includeTaxDeduction: false,
  includeExtraExpenses: false,
  extraExpensesAmount: '50000',
  extraExpensesDescription: 'Подготовительные курсы',
  universityLocation: 'hometown',
  universityType: 'public'
};

// Определение типов образования
export const educationLevels = [
  { id: 'bachelor', name: 'Бакалавриат', years: 4, avgCost: 150000 },
  { id: 'specialist', name: 'Специалитет', years: 5, avgCost: 175000 },
  { id: 'master', name: 'Магистратура', years: 2, avgCost: 200000 },
  { id: 'postgraduate', name: 'Аспирантура', years: 3, avgCost: 250000 },
  { id: 'vocational', name: 'Среднее профессиональное', years: 3, avgCost: 80000 },
  { id: 'additional', name: 'Дополнительное образование', years: 1, avgCost: 100000 }
];

// Определение типов университетов
export const universityTypes = [
  { id: 'public', name: 'Государственный вуз', costMultiplier: 1 },
  { id: 'private', name: 'Частный вуз', costMultiplier: 1.8 },
  { id: 'elite', name: 'Престижный вуз', costMultiplier: 2.5 }
];

// Определение локаций университетов
export const universityLocations = [
  { id: 'hometown', name: 'В родном городе', livingExpensesMultiplier: 1 },
  { id: 'other_city', name: 'В другом городе', livingExpensesMultiplier: 1.5 },
  { id: 'moscow_spb', name: 'Москва/Санкт-Петербург', livingExpensesMultiplier: 2 }
];

/**
 * EducationSavingsCalculatorPage Component - Калькулятор расчета накоплений на образование
 */
const EducationSavingsCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => {
    const dummyTool = {
      id: 'education-savings-calculator',
      name: 'Калькулятор накоплений на образование',
      metaTitle: 'Калькулятор накоплений на образование | Gensio',
      metaDescription: 'Рассчитайте необходимую сумму накоплений для образования вашего ребенка с учетом инфляции, доходности инвестиций, государственных программ поддержки и налоговых вычетов в России.'
    };
    return getToolById('education-savings-calculator') || dummyTool;
  }, []);
  
  const pageTitle = tool?.metaTitle || 'Калькулятор накоплений на образование | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте необходимую сумму накоплений для образования вашего ребенка с учетом инфляции, доходности инвестиций, государственных программ поддержки и налоговых вычетов в России.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [savingsData, setSavingsData] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedPlans, setSavedPlans] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных планов из localStorage ---
  useEffect(() => {
    const savedPlansData = localStorage.getItem('savedEducationPlans');
    if (savedPlansData) {
      try {
        setSavedPlans(JSON.parse(savedPlansData));
      } catch (e) {
        console.error("Error loading saved education plans:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'bachelor':
          newState.educationLevel = 'bachelor';
          newState.educationDuration = '4';
          newState.tuitionFeePerYear = '150000';
          break;
        case 'master':
          newState.educationLevel = 'master';
          newState.educationDuration = '2';
          newState.tuitionFeePerYear = '200000';
          break;
        case 'maternity-capital':
          newState.includeMaternityCapital = true;
          break;
        case 'tax-deduction':
          newState.includeTaxDeduction = true;
          break;
        case 'moscow':
          newState.universityLocation = 'moscow_spb';
          newState.livingExpensesPerYear = '240000';
          break;
        case 'preschool':
          newState.yearsUntilStart = '12';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setSavingsData([]);
    setErrors({});

  }, [subToolId]);

  // Сохранение плана накоплений в localStorage
  const savePlan = (name) => {
    if (!results) return;

    const newSavedPlan = {
      id: Date.now().toString(),
      name: name || `План ${formData.educationLevel} через ${formData.yearsUntilStart} лет`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedPlans = [...savedPlans, newSavedPlan];
    setSavedPlans(updatedSavedPlans);
    localStorage.setItem('savedEducationPlans', JSON.stringify(updatedSavedPlans));

    return newSavedPlan;
  };

  // Удаление сохраненного плана
  const deleteSavedPlan = (id) => {
    const updatedSavedPlans = savedPlans.filter(plan => plan.id !== id);
    setSavedPlans(updatedSavedPlans);
    localStorage.setItem('savedEducationPlans', JSON.stringify(updatedSavedPlans));
  };

  // Загрузка сохраненного плана
  const loadSavedPlan = (id) => {
    const planToLoad = savedPlans.find(plan => plan.id === id);
    if (planToLoad) {
      setFormData(planToLoad.formData);
      setResults(planToLoad.results);
      // Восстанавливаем данные для графика
      setSavingsData(planToLoad.results.savingsDataChart || []);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <GraduationCap className="w-8 h-8 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор накоплений на образование'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              setSavingsData={setSavingsData}
              educationLevels={educationLevels}
              universityTypes={universityTypes}
              universityLocations={universityLocations}
              savePlan={savePlan}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <SavingsChart 
                  savingsData={savingsData} 
                  results={results}
                />
              </>
            )}

            {/* --- Информационный контент об образовании и накоплениях --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              educationLevels={educationLevels}
              universityTypes={universityTypes}
              universityLocations={universityLocations}
            />

            {/* --- Панель сравнения планов --- */}
            <ComparisonSidebar 
              savedPlans={savedPlans}
              deleteSavedPlan={deleteSavedPlan}
              loadSavedPlan={loadSavedPlan}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }
      `}</style>
    </div>
  );
};

export default EducationSavingsCalculatorPage;